import { useTranslation } from '@axo/shared/i18n';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AcceptDeclineButtons } from '../components/AcceptDeclineButtons/AcceptDeclineButtons';

type BackendEmailValidationResult = {
  Input: { Email: string; Local: string; Host: string };
  IsValid: boolean;
  IsRisky: boolean;
  Suggestion: { Host: string };
};

type UseEmailValidation = {
  baseUrl: string;
  email: string;
  setEmail: (value: string) => void;
};

export const useEmailValidation = ({
  baseUrl,
  email,
  setEmail,
}: UseEmailValidation) => {
  const { t } = useTranslation();

  const [shouldValidateEmail, setShouldValidateEmail] = useState(false);

  const [isValid, setIsValid] = useState(true);
  const [isValidating, setIsValidating] = useState(false);
  const [emailSuggestionText, setEmailSuggestionText] = useState<
    string | undefined
  >();
  const [emailSuggestion, setEmailSuggestion] = useState('');

  const enableEmailValidation = () => setShouldValidateEmail(true);
  const disableEmailValidation = () => setShouldValidateEmail(false);

  const validateEmail = useCallback(async () => {
    setIsValidating(true);
    try {
      const response = await fetch(
        `${baseUrl}/email-validation/validate?email=${email}`
      );

      const {
        Input: { Local },
        Suggestion: { Host },
        IsValid,
      } = (await response.json()) as BackendEmailValidationResult;

      setIsValidating(false);
      setIsValid(IsValid);

      if (Host) {
        const suggest = `${Local}@${Host}`;
        setEmailSuggestionText(t('Email.suggestion', { email: suggest }));
        setEmailSuggestion(suggest);

        return suggest;
      }

      setEmailSuggestionText(undefined);
      setEmailSuggestion('');
    } catch (error) {
      console.log('error', error);
      setIsValidating(false);
      setIsValid(false);
    }
  }, [email, baseUrl, t]);

  useEffect(() => {
    if (shouldValidateEmail) {
      disableEmailValidation();
      validateEmail();
    }
  }, [shouldValidateEmail, validateEmail]);

  const onPromptAcceptClick = useCallback(() => {
    setEmail(emailSuggestion);
    setEmailSuggestionText(undefined);
    setIsValid(true);
  }, [setEmail, emailSuggestion, setEmailSuggestionText]);

  const onPromptDeclineClick = useCallback(() => {
    setEmailSuggestionText(undefined);
  }, [setEmailSuggestionText]);

  const EmailPrompt = React.memo(() => (
    <AcceptDeclineButtons
      isVisible={!!emailSuggestionText}
      onAccept={onPromptAcceptClick}
      onDecline={onPromptDeclineClick}
    />
  ));

  const hasError = useMemo(() => {
    return isValid === false;
  }, [isValid]);

  const formError = useMemo(() => {
    if (hasError || emailSuggestionText) {
      return {
        type: 'manual',
        message: emailSuggestionText
          ? emailSuggestionText
          : t('Email.error.pattern'),
      };
    }
    return undefined;
  }, [hasError, emailSuggestionText, t]);

  return useMemo(
    () => ({
      isValid,
      isValidating,
      hasError,
      formError,
      EmailPrompt,
      enableEmailValidation,
    }),
    [isValidating, EmailPrompt, hasError, isValid, formError]
  );
};
